import { AsyncPipe, CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
  WritableSignal,
} from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import {
  ButtonComponent,
  DatePickerComponent,
  IOptionItem,
  IPaginationEvent,
  ModalService,
  PaginationComponent,
  TableComponent,
} from '@ui-lib/components';
import { TranslateModule } from '@ngx-translate/core';
import { ScannerService } from './scanner.service';
import { ScannerCreateModalComponent } from './scanner-create-modal/scanner-create-modal.component';
import { SCANNER_TABLE_COLUMNS } from './scanner.consts';
import { FormControl, FormGroup, ReactiveFormsModule } from '@angular/forms';
import { FacilitySelectComponent } from '../../shared/components/facility-select/facility-select.component';
import {
  FALLBACK_SCAN_STATUS_BADGE,
  IScannerFormFilters,
  SCAN_STATUS_BADGE,
  ScanStatusBadge,
  ScanStatusId,
  SearchScans,
} from './scanner.model';
import { LoadingState } from '../../shared/types/loading.type';
import { take } from 'rxjs';
import { PermissionService } from '../../core/services/user/permission.service';
import { UserPermissionEnum } from '../../core/models/user-permissions.enum';
import { PageToolbarComponent } from '../../shared/components/page-toolbar/page-toolbar.component';
import { PageToolbarAction } from '../../shared/components/page-toolbar/page-toolbar.model';

const CREATE_SCAN_QUERY_PARAM = 'create';

@Component({
  selector: 'mon-scanner',
  templateUrl: './scanner.component.html',
  styleUrls: ['./scanner.component.scss'],
  imports: [
    CommonModule,
    RouterModule,
    ButtonComponent,
    TranslateModule,
    TableComponent,
    PaginationComponent,
    AsyncPipe,
    ReactiveFormsModule,
    DatePickerComponent,
    FacilitySelectComponent,
    PageToolbarComponent,
  ],
  providers: [ScannerService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ScannerComponent implements OnInit {
  protected readonly scannerService = inject(ScannerService);

  private readonly modal = inject(ModalService);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly destroyRef = inject(DestroyRef);
  private readonly permissionService = inject(PermissionService);

  protected readonly currentPage: WritableSignal<number> = signal(1);

  protected readonly scannerFormFilters = new FormGroup<IScannerFormFilters>({
    facilityId: new FormControl<IOptionItem | null>(null),
    datePeriod: new FormControl<string[] | null>(null),
  });

  protected readonly SCANNER_COLUMNS = SCANNER_TABLE_COLUMNS;
  protected readonly LoadingState = LoadingState;
  protected readonly canManageScans = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.ScansManage),
    { initialValue: false },
  );
  protected readonly canReadFacilities = toSignal(
    this.permissionService.hasPermission(UserPermissionEnum.FacilityRead),
    { initialValue: false },
  );
  protected get canCreateScans(): boolean {
    return this.canManageScans() && this.canReadFacilities();
  }

  protected get toolbarActions(): PageToolbarAction[] {
    return this.canCreateScans
      ? [
          {
            id: 'create',
            icon: 'scanner',
            titleTranslateKey: 'monitoring.scanner.create',
          },
        ]
      : [];
  }

  protected onToolbarAction(actionId: string): void {
    if (actionId === 'create') {
      this.openCreate();
    }
  }

  public ngOnInit(): void {
    this.scannerFormFilters.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((filters) => {
        this.currentPage.set(1);

        const transformedDatePeriod = filters.datePeriod
          ? {
              startDateTime: filters.datePeriod[0],
              endDateTime: filters.datePeriod[1],
            }
          : undefined;
        this.scannerService.updateScannerRequest({
          facilityId: this.canReadFacilities() ? filters.facilityId?.id : undefined,
          datePeriod: transformedDatePeriod,
          skip: 0,
        });
      });

    this.route.queryParamMap.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((params) => {
      if (params.get(CREATE_SCAN_QUERY_PARAM) !== 'true') {
        return;
      }

      this.openCreate();
      void this.router.navigate([], {
        relativeTo: this.route,
        queryParams: {
          [CREATE_SCAN_QUERY_PARAM]: null,
        },
        queryParamsHandling: 'merge',
        replaceUrl: true,
      });
    });
  }

  public get isFiltersActive(): boolean {
    const { facilityId, datePeriod } = this.scannerFormFilters.getRawValue();
    return (this.canReadFacilities() && facilityId !== null) || datePeriod !== null;
  }

  public get selectedFiltersCount(): number {
    const { facilityId, datePeriod } = this.scannerFormFilters.getRawValue();
    return (facilityId !== null ? 1 : 0) + (datePeriod !== null ? 1 : 0);
  }

  public asSearchScan = (searchScan: unknown): SearchScans => searchScan as SearchScans;

  public openCreate(): void {
    if (!this.canCreateScans) {
      return;
    }

    this.modal
      .open<void, string>({
        component: ScannerCreateModalComponent,
        customContainerClass: 'scanner-new-scan-modal',
      })
      .afterClose$.pipe(take(1), takeUntilDestroyed(this.destroyRef))
      .subscribe((scanId) => {
        if (scanId) {
          void this.openDetails(scanId);
        }
      });
  }

  public openDetails(scanId: string): Promise<boolean> {
    return this.router.navigate([scanId], { relativeTo: this.route });
  }

  public onPageChange(paginator: IPaginationEvent): void {
    this.currentPage.set(paginator.pageIndex);

    this.scannerService.updateScannerRequest({
      skip: (paginator.pageIndex - 1) * paginator.pageSize,
      take: paginator.pageSize,
    });
  }

  public clearAllFilters(): void {
    this.scannerFormFilters.reset();
  }

  protected getScanStatusBadge(statusId: number): ScanStatusBadge {
    return SCAN_STATUS_BADGE[statusId as ScanStatusId] ?? FALLBACK_SCAN_STATUS_BADGE;
  }
}
